'use strict';

import * as LoginEncoder from '@techsee/techsee-common/lib/login-info-encoder';
import {IBrowserUtilsService} from '@techsee/techsee-client-infra/lib/services/BrowserUtilsService';
import {IAuthService as IAuthApiService} from '@techsee/techsee-client-infra/lib/services/AuthService';

import EventEmitter from 'events';

interface LoginResponse {
    token: string;
    keepLogged?: boolean;
    confirmLogin?: boolean;
}

interface Token {
    token: string;
    authId?: string;
}

export interface IAuthService extends EventEmitter {
    login(userName: string, password: string, loginConfirmed?: boolean): Promise<string>;
    loginSubUser(accountId: string): Promise<void>;
    getAuthToken(): string;
    setAuthToken(token: Token): void;
    clearAuthToken(): void;
    loginWithOneTimeToken(ott: string): Promise<{isRedeemed: boolean}>;
    logout(accountId: string, userId: string, returnURL?: string): Promise<any>;
    logoutWithoutSSO(accountId: string, userId: string): Promise<any>;
}

export class AuthService extends EventEmitter implements IAuthService {
    private _browserUtilsService: IBrowserUtilsService;
    private _authApiService: IAuthApiService;

    constructor(browserUtilsService: IBrowserUtilsService, authApiService: IAuthApiService) {
        super();

        this._browserUtilsService = browserUtilsService;

        this._authApiService = authApiService;

        this.loginWithOneTimeToken = this.loginWithOneTimeToken.bind(this);
        this.getAuthToken = this.getAuthToken.bind(this);
        this.setAuthToken = this.setAuthToken.bind(this);
        this.clearAuthToken = this.clearAuthToken.bind(this);
        this.logoutWithoutSSO = this.logoutWithoutSSO.bind(this);
    }

    /**
     * Make a login request
     *
     * @param  {string} userName   Username or email
     * @param  {string} password   User password
     * @return {Object}            The request Promise
     */
    login(userName: string, password: string, loginConfirmed = false) {
        const loginData = LoginEncoder.encode(userName, password);

        return this._authApiService.login(loginData, false, loginConfirmed).then((res: LoginResponse) => {
            if (res.confirmLogin === true) {
                throw {confirmLogin: true};
            }

            this.setAuthToken({token: res.token});

            return res.token;
        });
    }

    loginSubUser(accountId: string) {
        const token = this.getAuthToken();

        return this._authApiService.loginSubUser(accountId, token).then((res) => {
            this.setAuthToken({token: res.token});
        });
    }

    getAuthToken() {
        const auth = this._getAuth();

        return auth?.token;
    }

    setAuthToken(token: Token) {
        this._browserUtilsService.saveToLocalStorage('auth', token);

        this.emit('newToken', token);
    }

    clearAuthToken() {
        this._browserUtilsService.saveToLocalStorage('auth', null);
    }

    loginWithOneTimeToken(ott: string) {
        if (!ott || this.getAuthToken()) {
            return Promise.resolve({isRedeemed: false});
        }

        return this._authApiService.redeemOneTimeToken(ott).then((token) => {
            if (token) {
                // Login successful, save the Token
                this.setAuthToken({token});

                return {isRedeemed: true};
            }

            return {isRedeemed: false};
        });
    }

    /**
     * Sign the use out of the system and cleanup the saved Token
     *
     * @return {Object} The request Promise
     */
    logout(accountId: string, userId: string, returnURL?: string) {
        const auth = this._getAuth();

        return this._authApiService.logout(accountId, userId, returnURL, auth?.token).then((res) => {
            // Logout successful, clear the saved Token
            this._browserUtilsService.saveToLocalStorage('auth', null);
            this._browserUtilsService.saveToSessionStorage('auth', null);

            return res;
        });
    }

    logoutWithoutSSO(accountId: string, userId: string) {
        const auth = this._getAuth();

        return this._authApiService.logout(accountId, userId, undefined, auth?.token, true).then((res) => {
            // Logout successful, clear the saved Token
            this._browserUtilsService.saveToLocalStorage('auth', null);
            this._browserUtilsService.saveToSessionStorage('auth', null);

            return res;
        });
    }

    private _getAuth = (): any => this._browserUtilsService.getFromLocalStorage('auth');
}
